import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { post, put, MICROSERVICES } from 'api';
import { IPolicy } from 'interfaces/Policy';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { openAlert } from 'modules/alertHandler';
import { listPoliciesAction } from 'modules/policyList';

import endpoints from './endpoints';
import { IPolicyState, IUpdatePolicyPayload } from './interfaces';

const initialState: IPolicyState = {
  errorMessage: '',
  loading: REQUEST_LIFE_CICLE.IDLE,
  isCreating: false,
};

const policyFulfilledAction = createAsyncThunk(
  'policyFulfilled',
  (payload, { dispatch }) => {
    dispatch(setIsCreating(false));
    dispatch(listPoliciesAction({}));
  },
);

export const createPolicyAction = createAsyncThunk<
  void,
  IPolicy,
  { rejectValue: IMyKnownError }
>('createPolicy', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await post<IPolicy>(MICROSERVICES.AUTH, {
      url: endpoints.createPolicy(),
      data: {
        name: payload.name,
        permissions: payload.permissions,
      },
    });
    dispatch(
      openAlert({
        title: 'Sucesso!',
        content: 'Perfil criado com sucesso',
        primaryButton: 'Ok',
        primaryButtonAction: policyFulfilledAction(),
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at create profile', error);
    return rejectWithValue(error.response.data);
  }
});

export const updatePolicyAction = createAsyncThunk<
  void,
  IUpdatePolicyPayload,
  { rejectValue: IMyKnownError }
>('updatePolicy', async ({ id, policy }, { dispatch, rejectWithValue }) => {
  try {
    await put<IPolicy>(MICROSERVICES.AUTH, {
      url: endpoints.updatePolicy(id),
      data: {
        name: policy.name,
        permissions: policy.permissions,
      },
    });
    dispatch(
      openAlert({
        title: 'Sucesso!',
        content: 'Perfil editado com sucesso',
        primaryButton: 'Ok',
        primaryButtonAction: policyFulfilledAction(),
      }),
    );
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    console.error('error at update profile', error);
    return rejectWithValue(error.response.data);
  }
});

const policySlice = createSlice({
  name: 'policy',
  initialState,
  reducers: {
    clearPolicy: () => initialState,
    setIsCreating: (state, action: PayloadAction<boolean, string>) => {
      state.isCreating = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createPolicyAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(createPolicyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(createPolicyAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || 'Erro no servidor';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(updatePolicyAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(updatePolicyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(updatePolicyAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || 'Erro no servidor';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });
  },
});

export const { setIsCreating, clearPolicy } = policySlice.actions;
export default policySlice.reducer;
